import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
    PASTE_COMMAND,
    DROP_COMMAND,
    COMMAND_PRIORITY_HIGH,
    $getSelection,
    $createTextNode,
    $insertNodes,
} from "lexical";
import { LinkNode } from "@lexical/link";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { isValidFile } from "../../../utils/helpers";
import { config } from "../../../config";
import { togglePopover } from "../../../redux/Slices/popoverSlice";
import { setUploadProgress, uploadAttachment } from "../../../redux/Slices/attachmentSlice";
import { showSnackBar } from "../../../redux/Slices/snackbarSlice";
import { fetchCardById } from "../../../redux/Slices/cardSlice";
import { INSERT_IMAGE_COMMAND } from "./ImagesPlugin";
import { fetchAttachmentUrl, createImageUrlWithId } from "../../../utils/attachmentUtils";

export default function PasteImagePlugin({ cardId, pageId, type, uploadContext = 'card' }) {
    const [editor] = useLexicalComposerContext();
    const dispatch = useDispatch();

    const insertImageInEditor = (imageUrl, fileName, attachmentId) => {
        // Store attachment ID in the image URL for future reference
        const imageUrlWithId = attachmentId ? createImageUrlWithId(imageUrl, attachmentId) : imageUrl;
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
            altText: fileName || "Uploaded Image",
            src: imageUrlWithId,
            attachmentId: attachmentId
        });
        // Don't close popover for feedback context (it's pinned)
        if (uploadContext !== 'feedback') {
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        }
    };

    const insertAttachmentLink = (fileUrl, fileName, attachmentId) => {
        editor.update(() => {
            const selection = $getSelection();
            if (selection) {
                const textNode = $createTextNode(fileName);
                const linkUrlWithId = attachmentId ? createImageUrlWithId(fileUrl, attachmentId) : fileUrl;
                const linkNode = new LinkNode(linkUrlWithId);
                linkNode.append(textNode);
                $insertNodes([linkNode]);
                // Don't close popover for feedback context (it's pinned)
                if (uploadContext !== 'feedback') {
                    dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
                }
            }
        });
    };

    const uploadToFeedback = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${config.API_URI}/v1/feedback/attachments`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            const errorMessage = errorData?.error || response.statusText || 'Failed to upload feedback attachment';
            throw new Error(errorMessage);
        }
        const data = await response.json();
        return data.attachment; // Return attachment object with _id, url, etc.
    };

    const uploadToPage = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${config.API_URI}/v1/pages/${pageId}/attachments`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            const errorMessage = errorData?.error || response.statusText || 'Failed to upload page attachment';
            throw new Error(errorMessage);
        }
        const data = await response.json();
        return data.attachment; // { url, name, fileHash, expiresAt, ... }
    };


    const handleFileUpload = async (file) => {
        console.log("PasteImagePlugin: handleFileUpload called for file:", file.name, file.type);
        try {
            if (!file) return;

            const validation = isValidFile(file);
            if (!validation.valid) {
                return dispatch(showSnackBar({ message: validation.error, type: "error" }));
            }

            dispatch(showSnackBar({ message: "Uploading files...", type: "uploading" }));

            if (uploadContext === 'feedback') {
                // Upload to feedback endpoint and insert plain URL (no signing, like profile pictures)
                const attachment = await uploadToFeedback(file);
                const url = attachment.url;
                if (!url) throw new Error('Upload succeeded but no URL returned');

                if (file.type.startsWith("image/")) {
                    insertImageInEditor(url, file.name);
                } else {
                    insertAttachmentLink(url, file.name);
                }
                dispatch(showSnackBar({ message: "Attachment uploaded", type: "success" }));
                return;
            }

            // Page context: upload to zoobbe-page-attachments bucket
            if (uploadContext === 'page') {
                const attachment = await uploadToPage(file);
                const url = attachment.url;
                if (!url) throw new Error('Upload succeeded but no URL returned');

                if (file.type.startsWith("image/")) {
                    insertImageInEditor(url, file.name);
                } else {
                    insertAttachmentLink(url, file.name);
                }
                dispatch(showSnackBar({ message: "Attachment uploaded", type: "success" }));
                return;
            }

            // Default: upload as card attachment
            dispatch(setUploadProgress({ percentage: "0", remainingTime: "" }));
            const response = await dispatch(uploadAttachment({ file, cardId, type }));

            if (response.meta.requestStatus !== "fulfilled") {
                throw new Error(response.payload?.message || "File upload failed.");
            }

            const attachmentId = response.payload.attachment._id;
            const { url: fetchedUrl } = await fetchAttachmentUrl(cardId, attachmentId);

            if (!fetchedUrl) throw new Error("Failed to retrieve uploaded file URL.");

            if (file.type.startsWith("image/")) {
                insertImageInEditor(fetchedUrl, file.name, attachmentId);
            } else {
                insertAttachmentLink(fetchedUrl, file.name, attachmentId);
            }

            dispatch(fetchCardById(cardId));
        } catch (error) {
            console.error("File upload error:", error);
            dispatch(showSnackBar({ message: error.message || "An error occurred while uploading the file.", type: "error" }));
        }
    };

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    console.log("PasteImagePlugin: PASTE_COMMAND triggered", { cardId, pageId, uploadContext });
                    const items = event.clipboardData?.items;
                    const files = event.clipboardData?.files;
                    
                    let handled = false;

                    if (items && items.length > 0) {
                        for (const item of items) {
                            console.log("PasteImagePlugin: Item type:", item.type);
                            if (item.type.startsWith("image/")) {
                                event.preventDefault();
                                const file = item.getAsFile();
                                console.log("PasteImagePlugin: Image item found, file:", file?.name);
                                if (file) handleFileUpload(file);
                                handled = true;
                            }
                        }
                    }

                    if (!handled && files && files.length > 0) {
                        console.log("PasteImagePlugin: Checking files fallback");
                        for (let i = 0; i < files.length; i++) {
                            const file = files[i];
                            console.log("PasteImagePlugin: File type:", file.type);
                            if (file.type.startsWith("image/")) {
                                event.preventDefault();
                                handleFileUpload(file);
                                handled = true;
                            }
                        }
                    }

                    console.log("PasteImagePlugin: PASTE_COMMAND handled:", handled);
                    return handled;
                },
                COMMAND_PRIORITY_HIGH
            ),
            editor.registerCommand(
                DROP_COMMAND,
                (event) => {
                    console.log("PasteImagePlugin: DROP_COMMAND triggered");
                    const files = event.dataTransfer?.files;
                    if (!files || files.length === 0) {
                        console.log("PasteImagePlugin: No dropped files found");
                        return false;
                    }

                    let handled = false;
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        console.log("PasteImagePlugin: Dropped file type:", file.type);
                        if (file.type.startsWith("image/")) {
                            event.preventDefault();
                            handleFileUpload(file);
                            handled = true;
                        }
                    }
                    console.log("PasteImagePlugin: DROP_COMMAND handled:", handled);
                    return handled;
                },
                COMMAND_PRIORITY_HIGH
            )
        );
    }, [editor, cardId, pageId, type, dispatch, uploadContext]);

    return null;
}
